import styled from "@emotion/styled";
import React, { useEffect, useMemo, useRef, useState } from "react";
import ReactQuill from "react-quill";
import theme from "../../theme";
import { UploadNoticeImages } from "./writeNotice";
import imageLoader from "../../assets/images/imageLoadergif.gif";
import { CustomAlert } from "../../components/customAlert";
import { WarningIcon } from "../../assets/icons/icons";
import { deleteNoticeImage, postNoticeImage } from "../../api/notice";
import { randomInt } from "../../utils/util";
import { useI18n } from "../../locales";

const { md } = theme.screen;

const toolbarOptions = [
  [{ header: [1, 2, 3, false] }],
  ["bold", "italic", "underline", "strike"],
  ["blockquote"],
  [{ list: "ordered" }, { list: "bullet" }],
  ["link", "image"],
  [{ color: [] }, { background: [] }],
  [{ align: [] }],
];

// 옵션에 상응하는 포맷, 추가해주지 않으면 text editor에 적용된 스타일을 볼수 없음
export const formats = [
  "header",
  "font",
  "size",
  "bold",
  "italic",
  "underline",
  "strike",
  "align",
  "blockquote",
  "list",
  "bullet",
  "link",
  "image", // 추가
  "color",
  "background",
  "align",
];

interface TextEditorProps {
  value: string;
  onChangeValue: any;
  beforeHtml: string;
  setBeforeHtml: any;
  counter: any;
  setCounter: any;
  imgFiles: UploadNoticeImages[]; // 새로 업로드된 이미지
  setImgFiles: any;
  initialImgFiles: UploadNoticeImages[]; // 기존 이미지 (수정)
  setInitialImgFiles: any;
  deleteImgFile: UploadNoticeImages[]; // 삭제할 이미지
  setDeleteImgFile: any;
  isNewWrite: boolean;
}

const TextEditor: React.FC<TextEditorProps> = (props) => {
  const { t } = useI18n();
  const {
    value,
    onChangeValue,
    setBeforeHtml,
    counter,
    setCounter,
    imgFiles,
    setImgFiles,
    initialImgFiles,
    setInitialImgFiles,
    setDeleteImgFile,
    isNewWrite, // 새 글쓰기인지 ? 수정하기인지 ?
  } = props;
  const QuillRef = useRef<ReactQuill>();
  const editor = QuillRef.current?.unprivilegedEditor;
  /**
   * 1024 = 1KB ,
   * 1024 ** 2 = 1MB
   * 1024 ** 3 = 1GB
   * 1024 ** 4 = 1TB
   */
  const sizeLimit = 1024 ** 2 * 5; // 5MB
  const [openAlert, setOpenAlert] = useState(false);
  const [alertContent, setAlertContent] = useState("");
  const acceptImageFileType = ["image/png", "image/jpg", "image/jpeg"]; // 허용할 파일 형식
  // const [deleteImgFile, setDeleteImgFile] = useState<UploadNoticeImages[]>(
  //   []
  // );

  const handleKeyPress = (event: React.KeyboardEvent<HTMLElement>) => {
    // 글자수 초과인지 확인
    if (
      editor &&
      editor?.getLength() >= 2000 &&
      event.key !== "Backspace"
    ) {
      // console.log(editor?.getLength());
      setAlertContent(t.notice.maxLengthExceeded);
      setOpenAlert(true);
      event.preventDefault();
      return;
    }

    // 이미지 삭제인지 확인
    if (
      editor &&
      editor?.getLength() <= 2000 &&
      event.key == "Backspace"
    ) {
      // imgFiles(신규)에서 에디터에 남아있는 이미지만 필터링
      if (imgFiles.length > 0) {
        let newImages = imgFiles.filter((el) =>
          editor.getHTML().includes(el.url)
        );
        setImgFiles(newImages);
      }

      // initialImgFiles(기존)에서 에디터에 남아있는 이미지만 필터링
      if (initialImgFiles.length > 0) {
        let initials = initialImgFiles.filter((el) =>
          editor.getHTML().includes(el.url)
        );
        setInitialImgFiles(initials);
      }

      // 삭제한 이미지만 필터링
      let deleteImage = imgFiles.filter(
        (el) => !editor.getHTML().includes(el.url)
      );
      for (let i = 0; i < deleteImage.length; i++) {
        setDeleteImgFile((prev: any) => [...prev, deleteImage[i]]);
      }

      let deleteInitialImage = initialImgFiles.filter(
        (el) => !editor.getHTML().includes(el.url)
      );
      for (let i = 0; i < deleteInitialImage.length; i++) {
        setDeleteImgFile((prev: any) => [...prev, deleteInitialImage[i]]);
      }

      // 로컬 관리
      let localData = localStorage.getItem("noticeImages");
      if (
        deleteImage &&
        deleteImage.length > 0 &&
        localData?.includes(deleteImage[0].url)
      ) {
        let newData = localData.replace(deleteImage[0].url, "");

        if (newData) {
          localStorage.setItem("noticeImages", newData);
        } else {
          localStorage.removeItem("noticeImages");
        }
      }

      // if (deleteImage.length > 0) {
      //   // console.log("삭제된 파일", deleteImage);
      //   // console.log("삭제할 파일 이름", deleteImage[0].url.substring(9));
      //   deleteNoticeImageFn(deleteImage[0].url.substring(9));
      // }
    }
  };

  const deleteNoticeImageFn = async (imageName: string) => {
    const res = await deleteNoticeImage({ fileName: imageName });
    if (res.httpStatus === "OK") {
      // console.log("이미지 삭제 성공");
      setDeleteImgFile([]);
    } else {
      // console.log("삭제 오류", res);
    }
  };

  // 공지 수정하기에서 이미 업로드된 이미지들 데이터 가져오기
  let prevImages: any = []; // 스트릭트 모드라 2개씩 저장되는거 방지용 ..
  let prevImage = {};
  useEffect(() => {
    if (!isNewWrite) {
      let contents = value;
      let imgCounter = contents.match(/<img/g);

      if (imgCounter && imgCounter.length === 0) {
        // 이미지가 0개
        return;
      } else if (imgCounter && imgCounter.length > 0) {
        // 이미지가 있음

        for (let i = 0; i < imgCounter.length; i++) {
          let addUrl = String(
            contents.substring(
              contents.indexOf(".com/") + 5,
              contents.indexOf('">')
            )
          );
          // console.log(addUrl);

          // console.log(contents.indexOf(".com/") + 5);
          // console.log(contents.indexOf('">'));

          // if (prevImages.findIndex((el: any) => el.url === addUrl) > -1) {
          //   return;
          // } else {

          // 저장된 이미지들 배열에 추가
          setInitialImgFiles((prev: any) => [
            ...prev,
            {
              index: randomInt(),
              url: addUrl,
            },
          ]);
          prevImage = { index: randomInt(), url: addUrl };
          prevImages = [...prevImages, prevImage];

          // 저장한 이미지는 컨텐츠에서 빼기
          contents = contents.substring(contents.indexOf('">') + 2); // 2 더하는 이유 : "> 포함하여 제거
          // console.log(contents);
        }
      }
      // }
    }
    return () => {
      setInitialImgFiles([]);
    };
  }, [isNewWrite]);

  useEffect(() => {
    // 입력된 글자수 계산
    if (value && editor) {
      setCounter(editor.getLength());
      // console.log(value, beforeHtml, editor?.getLength(), !editor);
    }
  }, [value, editor, counter]);

  // useEffect(() => {
  //   // 이미지 업로드 함수
  //   const imageHandler = () => {
  //     const input = document.createElement("input");
  //     input.setAttribute("type", "file");
  //     // input.setAttribute("accept", "image/*");
  //     input.setAttribute("accept", "image/png, image/jpg ,image/jpeg");
  //     input.click();

  //     const contentsEditor = QuillRef?.current?.getEditor();
  //     const range = contentsEditor?.getSelection();
  //     input.addEventListener("change", async () => {
  //       if (
  //         // 파일 형식 확인
  //         input.files &&
  //         !acceptImageFileType.includes(input.files[0].type)
  //       ) {
  //         setAlertContent(
  //           "파일 형식을 확인해주세요.\n업로드 가능한 파일 형식은 png, jpg, jpeg입니다."
  //         );
  //         setOpenAlert(true);
  //         return;
  //       }
  //       if (
  //         // 글자수 확인
  //         editor &&
  //         editor.getLength() > 2000
  //       ) {
  //         setAlertContent("2000자를 초과하였습니다.");
  //         setOpenAlert(true);
  //         return;
  //       } else if (
  //         // 첨부된 이미지 수 확인
  //         imgFiles.length >= 5
  //       ) {
  //         setAlertContent("이미지는 최대 5장까지만 첨부가 가능합니다.");
  //         setOpenAlert(true);
  //         return;
  //       } else if (
  //         // 첨부된 이미지 파일의 용량 확인
  //         input.files &&
  //         input.files[0].size > sizeLimit
  //       ) {
  //         setAlertContent("이미지는 최대 5MB까지만 첨부가 가능합니다.");
  //         setOpenAlert(true);
  //         return;
  //       } else {
  //         if (input.files && QuillRef.current && range && contentsEditor) {
  //           const file = input.files[0];
  //           // console.log("file", file);
  //           contentsEditor.insertEmbed(range.index, "image", imageLoader);

  //           const formData = new FormData();
  //           formData.append("file", file, file.name);
  //           // console.log("formData", formData);
  //           const res = await postNoticeImage(formData);
  //           const imgUrl = res.result;
  //           contentsEditor.deleteText(range.index, 1);
  //           contentsEditor.insertEmbed(
  //             // 서버에 저장 후 url 받아서 다시 설정
  //             range.index,
  //             "image",
  //             `${process.env.REACT_APP_BASE_URL}/${imgUrl}`
  //           );

  //           // 이미지들 저장하기
  //           setImgFiles((prev: any) => [
  //             ...prev,
  //             { index: range.index, file, url: imgUrl },
  //           ]);

  //           if (localStorage.getItem("noticeImages") !== null) {
  //             let prev = localStorage.getItem("noticeImages") + imgUrl;
  //             localStorage.setItem("noticeImages", prev);
  //           } else {
  //             localStorage.setItem("noticeImages", imgUrl);
  //           }
  //         }
  //       }
  //     });
  //   };

  //   if (QuillRef.current) {
  //     const toolbar = QuillRef.current.getEditor().getModule("toolbar");
  //     toolbar.addHandler("image", imageHandler);
  //   }
  // }, [imgFiles]);

  useEffect(() => {
    // 이미지 업로드 핸들러 함수
    const imageHandler = () => {
      const input = document.createElement("input");
      input.setAttribute("type", "file");
      input.setAttribute("accept", "image/png, image/jpg ,image/jpeg");
      input.click();

      const contentsEditor = QuillRef?.current?.getEditor();
      const range = contentsEditor?.getSelection();

      input.addEventListener("change", async () => {
        if (
          input.files &&
          !acceptImageFileType.includes(input.files[0].type)
        ) {
          setAlertContent(
            t.notice.invalidFileFormat
          );
          setOpenAlert(true);
          return;
        }
        if (editor && editor.getLength() > 2000) {
          setAlertContent(t.notice.maxLengthExceeded);
          setOpenAlert(true);
          return;
        } else if (imgFiles.length >= 5) {
          setAlertContent(t.notice.maxImagesExceeded);
          setOpenAlert(true);
          return;
        } else if (input.files && input.files[0].size > sizeLimit) {
          setAlertContent(t.notice.maxSizeExceeded);
          setOpenAlert(true);
          return;
        } else {
          if (input.files && QuillRef.current && range && contentsEditor) {
            const file = input.files[0];
            contentsEditor.insertEmbed(range.index, "image", imageLoader);

            const formData = new FormData();
            formData.append("file", file, file.name);

            const res = await postNoticeImage(formData);
            const imgUrl = res.result;
            contentsEditor.deleteText(range.index, 1);
            contentsEditor.insertEmbed(
              range.index,
              "image",
              `${process.env.REACT_APP_BASE_URL}/${imgUrl}`
            );

            setImgFiles((prev: any) => [
              ...prev,
              { index: range.index, file, url: imgUrl },
            ]);

            if (localStorage.getItem("noticeImages") !== null) {
              let prev = localStorage.getItem("noticeImages") + imgUrl;
              localStorage.setItem("noticeImages", prev);
            } else {
              localStorage.setItem("noticeImages", imgUrl);
            }
          }
        }
      });
    };

    if (QuillRef.current) {
      const contentsEditor = QuillRef.current.getEditor();

      // MutationObserver를 사용하여 DOM 변경 감지
      const observer = new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          if (mutation.type === "childList") {
            console.log("DOM 변경 감지:", mutation);
          }
        });
      });

      // Quill 에디터의 root 요소를 관찰
      observer.observe(contentsEditor.root, {
        childList: true,
        subtree: true,
      });

      // 기존 툴바 핸들러 등록
      const toolbar = contentsEditor.getModule("toolbar") as any;
      toolbar.addHandler("image", imageHandler);

      return () => {
        observer.disconnect(); // 컴포넌트 언마운트 시 감지 중단
      };
    }
  }, [imgFiles]);

  useEffect(() => {
    // 새로고침해서 데이터 날라간 경우 체크하여 이미지 삭제하기
    let localData = localStorage.getItem("noticeImages");
    if (localData) {
      if (!value.includes(localData)) {
        // console.log(localData);
        let imgCounter = localData.match(/userimg/g);
        // console.log(imgCounter);

        if (imgCounter && imgCounter.length > 0) {
          let contents = localData;
          for (let i = 0; i < imgCounter.length; i++) {
            contents.lastIndexOf("/userimg/");
            let deleteName = contents.substring(
              contents.lastIndexOf("/userimg/") + 9
            );
            deleteNoticeImageFn(deleteName);
            contents = contents.substring(
              0,
              contents.lastIndexOf("/userimg/")
            );

            let newData = contents.replace(
              contents.substring(contents.lastIndexOf("/userimg/")),
              ""
            );
            // console.log("반복문 contents", contents);
            if (newData) {
              localStorage.setItem("noticeImages", newData);
            } else {
              localStorage.removeItem("noticeImages");
            }
          }
        }
      }
    }
  }, []);

  const modules = useMemo(
    () => ({
      toolbar: {
        container: toolbarOptions,
      },
    }),
    []
  );

  // console.log(localStorage.getItem("noticeImages"));
  // localStorage.removeItem("noticeImages");
  // console.log("현재 내용", value);
  // console.log(value, beforeHtml);
  // console.log(value, beforeHtml, editor?.getLength(), !editor, counter);
  // console.log("editor", editor?.getHTML());
  // console.log("기존 이미지들", initialImgFiles);
  // console.log("저장된 새 이미지들", imgFiles);
  // console.log("삭제할 이미지", deleteImgFile);

  return (
    <div style={{ flex: 1 }}>
      <CustomQuill
        // {...rest}
        ref={(element) => {
          if (element !== null) {
            QuillRef.current = element;
          }
        }}
        value={value || ""}
        theme="snow"
        // onKeyPress={handleKeyPress}
        onKeyDown={handleKeyPress}
        placeholder={t.notice.contentPlaceholder}
        onChange={(_, __, ___, editor) => {
          setBeforeHtml(editor.getText());
          setCounter(editor.getLength());
          if (editor.getText().length <= 2001) {
            onChangeValue(editor.getHTML());
          }
        }}
        modules={modules}
        formats={formats}
      />
      {/* 글자수 계산 */}
      {editor ? (
        <LengthCount>{`${!value ? 0 : counter - 1} / 2000`}</LengthCount>
      ) : (
        <LengthCount>{`${
          value
            .replaceAll("</p>", ".")
            .replaceAll("</h1>", ".")
            .replaceAll("</h2>", ".")
            .replaceAll("</h3>", ".")
            .replaceAll("</li>", ".")
            .replaceAll("</blockquote>", ".")
            .replace(/(<([^>]+)>)/gi, "").length > 0
            ? value
                .replaceAll("</p>", ".")
                .replaceAll("</h1>", ".")
                .replaceAll("</h2>", ".")
                .replaceAll("</h3>", ".")
                .replaceAll("</li>", ".")
                .replaceAll("</blockquote>", ".")
                .replaceAll("&nbsp;", ".")
                .replace(/(<([^>]+)>)/gi, "").length - 1
            : 0
        } / 2000`}</LengthCount>
      )}
      <Precautions>
        <p>{t.notice.imageFormatInfo}</p>
        <p>{t.notice.imageSizeInfo}</p>
      </Precautions>
      <CustomAlert
        open={openAlert}
        onPressOK={() => {
          setOpenAlert(false);
          setAlertContent("");
        }}
        title={t.notice.confirmTitle}
        content={alertContent}
        confirmText={t.notice.confirm}
        icon={<WarningIcon />}
      />
    </div>
  );
};

const CustomQuill = styled(ReactQuill)`
  .ql-editor strong {
    font-weight: 700;
  }
  .ql-editor em {
    font-style: italic;
  }
  .ql-editor u {
    text-decoration: underline;
  }
  .ql-toolbar {
    margin-bottom: 10px;
    border: none;
    background-color: ${theme.color.white};
    border-radius: 10px;
  }
  .ql-editor {
    border: 1;
    background-color: ${theme.color.white};
    border: none;
    border-radius: 10px;
    height: 710px;
    /* max-height: 667px;
    min-height: 350px; */
    /* height: calc(100vh - 400px); */
    padding: 30px;

    ::before {
      padding-left: 15px;
    }
  }
  .ql-container {
    border: none;
    @media (max-width: ${md}px) {
    }
  }

  @media (max-width: ${md}px) {
    max-height: none;

    .ql-editor {
      height: auto;
      padding: 20px;
      max-height: none;
      min-height: calc(100vh - 350px);
    }
  }
`;

const LengthCount = styled.div`
  text-align: right;
  padding: 10px 0;

  @media (max-width: ${md}px) {
    font-size: 12px;
  }
`;

const Precautions = styled.div`
  p {
    font-size: 12px;
    line-height: 16px;
    color: ${theme.color.neutral_500};
  }

  @media (max-width: ${md}px) {
    p {
      font-size: 10px;
      line-height: 14px;
    }
  }
`;

export default TextEditor;
